/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect } from 'react'
import { Link } from 'react-router'
import { useServiceContext } from '../../../../root'
import { useRcmTableData } from '../../../../hooks/useRcmTableData'
import { RcmTable } from '../../../rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell } from '../../../table/TableComponents'
import { ReportActionMenu } from './ReportActionMenu'
import DocIcon from '../../../shared/DocIcon'
import ValidDocIndicator from '../../../shared/ValidDocIndicator'
import { FsiType, AclLevel } from '../../../../constants/fsi'
import { getFileDownloadUrl } from '../../../../services/api/files'

interface ReportItem {
  id: string
  name: string
  type: string
  updatedOn: string
  updatedAt: number
  acl: string
  valid: boolean
  workflow?: any[]
  actionFlags?: {
    canDownloadSource?: boolean
    canManageAcl?: boolean
    canRename?: boolean
    canObsolete?: boolean
    canUnobsolete?: boolean
  }
  obsolete?: boolean
  isFavorite?: boolean
  systemFolderType?: string
}

export const ReportsTable: React.FC = () => {
  const {
    service: {
      filter: { translate, date }
    }
  } = useServiceContext()
  const tableData = useRcmTableData()

  useEffect(() => {
    tableData.refreshTable()
    const handleUpdate = () => {
      tableData.refreshTable()
    }
    window.addEventListener('updateReports', handleUpdate)
    window.addEventListener('updateFavorites', handleUpdate)

    return () => {
      window.removeEventListener('updateReports', handleUpdate)
      window.removeEventListener('updateFavorites', handleUpdate)
    }
  }, [])

  return (
    <RcmTable
      className="chita-table fsiTable dashboard-table"
      noResultsText="NO_RESULTS_FILTERED"
      hidePaginate={true}
      disableOnInitLoad={false}
      TableFooter={
        <tr>
          <td colSpan={3} className="view-all">
            <Link to="/browser/0?type=REPORTS" className="hidden-link">
              {translate('VIEW_ALL')}
            </Link>
          </td>
        </tr>
      }
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="9609cc50-234c-4b9a-b840-26f3da09efab">{translate('NAME')}</TableHeadCell>
          <TableHeadCell id="6a0ce50a-d3dc-4d0a-bb0d-3a41921150b3">{translate('TABLE_TH_UPDATED_ON')}</TableHeadCell>
          <TableHeadCell id="02caaf07-ccdc-4b56-9546-ba7c1af12185" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row }: { row: ReportItem }) => (
        <tr key={row.id}>
          <td className="name clipped-cell with-thumb">
            <div className="d-flex align-left">
              {row.type !== FsiType.FOLDER && <ValidDocIndicator fsi={row} />}
              <DocIcon
                fsi={row}
                onClickDownload={async () => {
                  if (![AclLevel.NONE, AclLevel.BROWSE].includes(row.acl as any)) {
                    try {
                      const data = await getFileDownloadUrl(Number(row.id))
                      if (data.url) {
                        window.location.href = data.url
                      }
                    } catch (error) {
                      console.error('Error downloading report:', error)
                    }
                  }
                }}
                inWorkflow={row.workflow && row.workflow.length > 0}
              />
              <div className="ellipsis" title={row.name}>
                {row.type === FsiType.PLACEHOLDER ||
                (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
                !row.valid ? (
                  <span>{row.name}</span>
                ) : (
                  <button
                    type="button"
                    className="text-link-btn word-break btn btn-link"
                    onClick={async () => {
                      if (![AclLevel.NONE, AclLevel.BROWSE].includes(row.acl as any)) {
                        try {
                          const data = await getFileDownloadUrl(Number(row.id))
                          if (data.url) {
                            window.location.href = data.url
                          }
                        } catch (error) {
                          console.error('Error downloading report:', error)
                        }
                      }
                    }}
                  >
                    {row.name}
                  </button>
                )}
              </div>
            </div>
          </td>
          <td className="narrow updated">{row.updatedAt ? date(row.updatedAt) : translate('NOT_AVAILABLE')}</td>
          <td className="actions">{row.valid && <ReportActionMenu fsi={row} />}</td>
        </tr>
      )}
    />
  )
}
